// Permission helpers mirroring the backend DealFlow.* permission catalog.
//
// The backend is the source of truth: every action endpoint checks
// the relevant policy via [Authorize(Policy = ...)]. These helpers exist
// so the UI can hide / disable affordances users can't act on.

import type { CriteriaLane, DealRoleCode } from "@/api/dealFlow/types";

/** DealFlow.* permission catalog. */
export const DEAL_FLOW_PERMISSIONS = {
  ViewDeal: "DealFlow.ViewDeal",
  AssignTeam: "DealFlow.AssignTeam",
  SelfClaimSlot: "DealFlow.SelfClaimSlot",
  MarkSlotNotApplicable: "DealFlow.MarkSlotNotApplicable",
  WriteUnderwritingTab: "DealFlow.WriteUnderwritingTab",
  WriteAnalystTab: "DealFlow.WriteAnalystTab",
  CertifyChecklistItem: "DealFlow.CertifyChecklistItem",
  EscalateChecklistItem: "DealFlow.EscalateChecklistItem",
  DeclineChecklistItem: "DealFlow.DeclineChecklistItem",
  WaiveChecklistItem: "DealFlow.WaiveChecklistItem",
  IssueLanePassFail: "DealFlow.IssueLanePassFail",
  VerifyAnalystWork: "DealFlow.VerifyAnalystWork",
  IssueApprovalLetter: "DealFlow.IssueApprovalLetter",
  AssignClosingCoordinator: "DealFlow.AssignClosingCoordinator",
  WriteClosingTab: "DealFlow.WriteClosingTab",
  ReleaseEscrow: "DealFlow.ReleaseEscrow",
  CreateLoan: "DealFlow.CreateLoan",
  ViewFailures: "DealFlow.ViewFailures",
  ResolveFailure: "DealFlow.ResolveFailure",
  ViewEscalations: "DealFlow.ViewEscalations",
  ResolveEscalation: "DealFlow.ResolveEscalation",
  ViewMetrics: "DealFlow.ViewMetrics",
  ViewAllMetrics: "DealFlow.ViewAllMetrics",
  OverrideGate: "DealFlow.OverrideGate",
  ReopenClosedDeal: "DealFlow.ReopenClosedDeal",
  // Writes to a Withdrawn or stage-Declined deal; everyone else gets 423.
  EditWithdrawnOrDeclinedDeal: "DealFlow.EditWithdrawnOrDeclinedDeal",
  // Admin-only undo for the reversible workflow actions.
  AdminUndo: "DealFlow.Admin.Undo",
  // Closing-gate BYPASS framework — admin-only break-glass to force-complete
  // a closing gate (Escrow Setup only today) for TEST deals so QA can reach loan
  // boarding without a real, funded escrow close.
  BypassClosingGate: "DealFlow.BypassClosingGate",
  // Deal Decline Workflow.
  ProposeDecline: "DealFlow.ProposeDecline",
  WithdrawDeclineProposal: "DealFlow.WithdrawDeclineProposal",
  ConfirmDecline: "DealFlow.ConfirmDecline",
} as const;

export type DealFlowPermission =
  (typeof DEAL_FLOW_PERMISSIONS)[keyof typeof DEAL_FLOW_PERMISSIONS];

const has = (perms: ReadonlySet<string>, p: DealFlowPermission) => perms.has(p);

/**
 * Whether the caller may assign any user to `roleCode`'s pill, or clear it,
 * from the popover's general controls. Mirrors the server
 * (`DealAssignmentService.UpsertAssignmentAsync`): only AssignTeam (ADMIN
 * holds every permission). A SelfClaimSlot holder claims through
 * `canSelfClaim` and clears through `canClearOwnPill`; the server refuses
 * them anything else.
 */
export function canAssignPill(perms: ReadonlySet<string>): boolean {
  return has(perms, DEAL_FLOW_PERMISSIONS.AssignTeam);
}

/**
 * Owner ruling 14, 2026-10-08: a self-claim holder may clear their own pill,
 * only while they are its current assignee and hold SelfClaimSlot. Mirrors the
 * server's clear branch; clearing someone else's claim still needs AssignTeam.
 */
export function canClearOwnPill(
  perms: ReadonlySet<string>,
  options: { callerIsCurrentAssignee: boolean },
): boolean {
  return has(perms, DEAL_FLOW_PERMISSIONS.SelfClaimSlot) && options.callerIsCurrentAssignee;
}

/**
 * Whether the caller may flip a pill to NotApplicable. Distinct from
 * AssignPill because the NA action sits behind its own permission.
 */
export function canMarkSlotNotApplicable(perms: ReadonlySet<string>): boolean {
  return has(perms, DEAL_FLOW_PERMISSIONS.MarkSlotNotApplicable);
}

/** The lane pills whose Not Applicable status decides which lanes the outcome counts. */
const LANE_PILL_ROLES: ReadonlySet<DealRoleCode> = new Set(["A2", "A3", "A4"]);

/**
 * Owner approval, 2026-10-13: once the deal outcome is finalized, an A2, A3 or
 * A4 pill cannot move into or out of Not Applicable (the server refuses with
 * 409 until an Admin Undo of the finalize). Changing who holds the pill is not
 * refused. Mirrors `DealAssignmentService.RefuseLaneApplicabilityChangeAfterFinalizeAsync`.
 */
export function isLaneApplicabilityLocked(
  roleCode: DealRoleCode,
  outcomeFinalized: boolean,
): boolean {
  return outcomeFinalized && LANE_PILL_ROLES.has(roleCode);
}

/** The pills that show the deal header's C1, B1 and A1 (owner rulings, 2026-10-09). */
export const HEADER_PILL_ROLES: ReadonlySet<DealRoleCode> = new Set(["C1", "B1", "A1"]);

/**
 * Owner rulings, 2026-10-09: the C1, B1 and A1 pills show the deal header's
 * C1, B1 and A1 and change only with them (assign and Reassign in the deal
 * header). The server's pill endpoints refuse these roles for every caller, so
 * the popover offers no edit on them. A2, A3, A4 and B2 are editable under the
 * checks above.
 */
export function isPillEditable(roleCode: DealRoleCode): boolean {
  return !HEADER_PILL_ROLES.has(roleCode);
}

/**
 * Whether the caller may self-claim an Unassigned slot whose role they
 * already hold. The pill widget surfaces a primary "Claim this slot"
 * button when this returns true.
 */
export function canSelfClaim(
  perms: ReadonlySet<string>,
  options: {
    roleCode: DealRoleCode;
    userRoleCodes: ReadonlyArray<string>;
  },
): boolean {
  return (
    has(perms, DEAL_FLOW_PERMISSIONS.SelfClaimSlot) &&
    options.userRoleCodes.includes(options.roleCode)
  );
}

// Simple boolean checks used by section components ----------------------

export const canViewDeal = (p: ReadonlySet<string>) => has(p, DEAL_FLOW_PERMISSIONS.ViewDeal);
export const canAssignTeam = (p: ReadonlySet<string>) => has(p, DEAL_FLOW_PERMISSIONS.AssignTeam);
export const canWriteUnderwritingTab = (p: ReadonlySet<string>) =>
  has(p, DEAL_FLOW_PERMISSIONS.WriteUnderwritingTab);
export const canWriteAnalystTab = (p: ReadonlySet<string>) =>
  has(p, DEAL_FLOW_PERMISSIONS.WriteAnalystTab);
export const canCertifyChecklistItem = (p: ReadonlySet<string>) =>
  has(p, DEAL_FLOW_PERMISSIONS.CertifyChecklistItem);
export const canEscalateChecklistItem = (p: ReadonlySet<string>) =>
  has(p, DEAL_FLOW_PERMISSIONS.EscalateChecklistItem);
export const canDeclineChecklistItem = (p: ReadonlySet<string>) =>
  has(p, DEAL_FLOW_PERMISSIONS.DeclineChecklistItem);
export const canWaiveChecklistItem = (p: ReadonlySet<string>) =>
  has(p, DEAL_FLOW_PERMISSIONS.WaiveChecklistItem);
export const canIssueLanePassFail = (p: ReadonlySet<string>) =>
  has(p, DEAL_FLOW_PERMISSIONS.IssueLanePassFail);
export const canVerifyAnalystWork = (p: ReadonlySet<string>) =>
  has(p, DEAL_FLOW_PERMISSIONS.VerifyAnalystWork);
export const canIssueApprovalLetter = (p: ReadonlySet<string>) =>
  has(p, DEAL_FLOW_PERMISSIONS.IssueApprovalLetter);
export const canAssignClosingCoordinator = (p: ReadonlySet<string>) =>
  has(p, DEAL_FLOW_PERMISSIONS.AssignClosingCoordinator);
export const canWriteClosingTab = (p: ReadonlySet<string>) =>
  has(p, DEAL_FLOW_PERMISSIONS.WriteClosingTab);
export const canReleaseEscrow = (p: ReadonlySet<string>) =>
  has(p, DEAL_FLOW_PERMISSIONS.ReleaseEscrow);
export const canViewFailures = (p: ReadonlySet<string>) =>
  has(p, DEAL_FLOW_PERMISSIONS.ViewFailures);
export const canResolveFailure = (p: ReadonlySet<string>) =>
  has(p, DEAL_FLOW_PERMISSIONS.ResolveFailure);
export const canOverrideGate = (p: ReadonlySet<string>) =>
  has(p, DEAL_FLOW_PERMISSIONS.OverrideGate);
export const canReopenClosedDeal = (p: ReadonlySet<string>) =>
  has(p, DEAL_FLOW_PERMISSIONS.ReopenClosedDeal);
export const canAdminUndo = (p: ReadonlySet<string>) =>
  has(p, DEAL_FLOW_PERMISSIONS.AdminUndo);

/**
 * Analyst-tab lane-ownership check for Section 2 (additional certifications)
 * and Section 3 (lane pass/fail), whose server checks admit the lane's role, A1
 * and ADMIN. The Section 1 checklist does not use it: its rows read
 * `checklistItemActions`, which mirrors the checklist service's wider rule
 * (owner approval, 2026-10-13).
 *
 * Lane owners:
 *   - A1 / Admin (OverrideGate)  → may edit any lane.
 *   - A2  → may edit A2.
 *   - A3  → may edit A3.
 *   - A4  → may edit A4.
 *   - All other roles (B1/B2/C0/C1/E1/E2/E4/B5) → read-only on every lane.
 */
export function getLaneEditPermission(
  perms: ReadonlySet<string>,
  options: {
    lane: CriteriaLane;
    userRoleCodes: ReadonlyArray<string>;
  },
): { canEdit: boolean; reason: string } {
  // Admin override (and A1 holding OverrideGate) edits any lane.
  if (has(perms, DEAL_FLOW_PERMISSIONS.OverrideGate)) {
    return { canEdit: true, reason: "Admin / override-gate access." };
  }
  // A1 holders edit A1 and may edit any analyst lane.
  if (options.userRoleCodes.includes("A1")) {
    return { canEdit: true, reason: "A1 underwriter holds cross-lane edit." };
  }
  // Lane-specific analyst owners.
  if (
    options.lane === "A2" &&
    options.userRoleCodes.includes("A2") &&
    has(perms, DEAL_FLOW_PERMISSIONS.WriteAnalystTab)
  ) {
    return { canEdit: true, reason: "A2 owns the Financial Analysis lane." };
  }
  if (
    options.lane === "A3" &&
    options.userRoleCodes.includes("A3") &&
    has(perms, DEAL_FLOW_PERMISSIONS.WriteAnalystTab)
  ) {
    return { canEdit: true, reason: "A3 owns the Legal Analysis lane." };
  }
  if (
    options.lane === "A4" &&
    options.userRoleCodes.includes("A4") &&
    has(perms, DEAL_FLOW_PERMISSIONS.WriteAnalystTab)
  ) {
    return { canEdit: true, reason: "A4 owns the Business Analysis lane." };
  }
  return {
    canEdit: false,
    reason: `You do not own the ${options.lane} lane. View-only.`,
  };
}

/** Roles `CriteriaChecklistService.EnsureLaneOwnership` admits on every lane:
 *  ADMIN, A1 and the E-tier reviewers E1, E2 and E4. Anyone else needs the
 *  lane's own role. */
const CHECKLIST_ANY_LANE_ROLES: ReadonlyArray<string> = ["ADMIN", "A1", "E1", "E2", "E4"];

/** The checklist-item actions a caller may take on a lane. */
export interface ChecklistItemActions {
  save: boolean;
  certify: boolean;
  decline: boolean;
  escalate: boolean;
  waive: boolean;
}

export const NO_CHECKLIST_ACTIONS: ChecklistItemActions = {
  save: false,
  certify: false,
  decline: false,
  escalate: false,
  waive: false,
};

/**
 * Owner approval, 2026-10-13: a checklist row offers exactly what the server
 * accepts. Each action needs its controller permission AND the service's role
 * check: the lane check for save, certify, decline and escalate (and the
 * escalation's reset-to-pending), and E2 or ADMIN for waive, on any lane. So E2
 * can waive and E4 can escalate from the analyst tabs, and an A2 cannot act on
 * the A1 lane.
 */
export function checklistItemActions(
  perms: ReadonlySet<string>,
  options: { lane: CriteriaLane; userRoleCodes: ReadonlyArray<string> },
): ChecklistItemActions {
  const roles = options.userRoleCodes;
  const onLane =
    CHECKLIST_ANY_LANE_ROLES.some((r) => roles.includes(r)) || roles.includes(options.lane);
  return {
    save: onLane && canWriteAnalystTab(perms),
    certify: onLane && canCertifyChecklistItem(perms),
    decline: onLane && canDeclineChecklistItem(perms),
    escalate: onLane && canEscalateChecklistItem(perms),
    waive: (roles.includes("E2") || roles.includes("ADMIN")) && canWaiveChecklistItem(perms),
  };
}
